/*
  slownote: ragged-edge image reveal.
  Large images enter from the bottom of the screen with a scalloped top edge
  that flattens out as they scroll into view.
*/
(() => {
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  const SELECTOR = [
    "[id$='__about_us'] img",
    "[id$='__leathers'] .group-block img",
    "[id$='__how'] .group-block-content :is(img, .placeholder-image)",
    "[id$='__product_list_nQbyNK'] img",
  ].join(',');
  const STEPS = 24;
  let items = [];
  let ticking = false;

  function collect() {
    items = Array.from(document.querySelectorAll(SELECTOR)).filter((el) => el.offsetWidth >= 160);
    update();
  }

  function shape(el, rect, viewport) {
    // 0 when the top edge touches the bottom of the screen, 1 once it has travelled 45% up
    const raw = (viewport - rect.top) / (viewport * 0.45);
    const progress = Math.min(Math.max(raw, 0), 1);
    if (progress >= 1 || rect.bottom < 0) {
      el.style.clipPath = '';
      return;
    }
    const eased = 1 - Math.pow(1 - progress, 3);
    const amplitude = Math.min(rect.height * 0.3, rect.width * 0.12) * (1 - eased);
    const arches = Math.max(2, Math.round(rect.width / 320));
    const points = [];
    for (let i = 0; i <= arches * STEPS; i++) {
      const x = i / (arches * STEPS);
      const y = amplitude * (1 - Math.abs(Math.sin(Math.PI * x * arches)));
      points.push(`${(x * 100).toFixed(2)}% ${y.toFixed(1)}px`);
    }
    points.push('100% 100%', '0% 100%');
    el.style.clipPath = `polygon(${points.join(',')})`;
  }

  function update() {
    ticking = false;
    const viewport = window.innerHeight;
    for (const el of items) shape(el, el.getBoundingClientRect(), viewport);
  }

  function onScroll() {
    if (!ticking) {
      ticking = true;
      requestAnimationFrame(update);
    }
  }

  // The theme scrolls .page-wrapper on desktop and the window on mobile, so listen in the capture phase
  document.addEventListener('scroll', onScroll, { capture: true, passive: true });
  window.addEventListener('resize', onScroll, { passive: true });
  document.addEventListener('shopify:section:load', collect);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', collect);
  else collect();
  window.addEventListener('load', collect);
})();

/*
  slownote: reviews carousel arrows.
  The row scrolls natively (scroll-snap); the arrows move it by one card.
*/
(() => {
  const ROW = "[id$='__testimonials'] .group-block > .group-block-content.layout-panel-flex--row";
  const CHEVRON = {
    prev: '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="m15 18-6-6 6-6"/></svg>',
    next: '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="m9 18 6-6-6-6"/></svg>',
  };

  function setup(row) {
    const host = row.parentElement;
    if (!host || host.querySelector(':scope > .sn-carousel-nav')) return;
    const nav = document.createElement('div');
    nav.className = 'sn-carousel-nav';
    nav.innerHTML =
      `<button type="button" data-dir="-1" aria-label="Previous reviews">${CHEVRON.prev}</button>` +
      `<button type="button" data-dir="1" aria-label="Next reviews">${CHEVRON.next}</button>`;
    host.appendChild(nav);
    const [prev, next] = nav.querySelectorAll('button');

    const step = () => {
      const card = row.querySelector(':scope > .group-block');
      return card ? card.getBoundingClientRect().width + 16 : row.clientWidth;
    };
    const refresh = () => {
      const max = row.scrollWidth - row.clientWidth;
      nav.hidden = max <= 2;
      prev.disabled = row.scrollLeft <= 2;
      next.disabled = row.scrollLeft >= max - 2;
      nav.style.top = row.offsetTop + 'px';
      nav.style.height = row.offsetHeight + 'px';
      row.style.setProperty('--sn-fade-r', next.disabled || nav.hidden ? '0px' : '96px');
      row.style.setProperty('--sn-fade-l', prev.disabled || nav.hidden ? '0px' : '96px');
    };
    nav.addEventListener('click', (e) => {
      const btn = e.target.closest('button');
      if (!btn) return;
      row.scrollBy({ left: Number(btn.dataset.dir) * step(), behavior: 'smooth' });
    });
    row.addEventListener('scroll', refresh, { passive: true });
    window.addEventListener('resize', refresh, { passive: true });
    refresh();
  }

  function init() {
    document.querySelectorAll(ROW).forEach(setup);
  }

  document.addEventListener('shopify:section:load', init);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
  window.addEventListener('load', init);
})();

/*
  slownote: anchors for the menu links (/#journals, /#about, /#contact).
*/
(() => {
  const MAP = { journals: "[id$='__product_list_nQbyNK']", about: "[id$='__about_us']", contact: "[id$='__contact']" };
  function anchors() {
    Object.entries(MAP).forEach(([id, sel]) => {
      const section = document.querySelector(sel);
      if (!section || document.getElementById(id)) return;
      const marker = document.createElement('span');
      marker.id = id;
      marker.style.cssText = 'display:block;height:0;scroll-margin-top:72px;';
      section.prepend(marker);
    });
    if (location.hash && document.getElementById(location.hash.slice(1))) {
      document.getElementById(location.hash.slice(1)).scrollIntoView();
    }
  }
  document.addEventListener('shopify:section:load', anchors);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', anchors);
  else anchors();
  window.addEventListener('load', anchors);
})();
